import { useEffect, useState } from "react";
import MainHeader from "@components/ui/MainHeader.jsx";
import {
  LockSleepCoach,
  ConsistancyChamp,
  WaistChange,
  WeightChange,
  BodyFat,
  SleepAvg,
  BodyProgressHero,
  BalancedPerformer,
} from "@utils/defaultImages.js";
import HeaderImage from "@assets/images/WeeklyProgressHeader.svg";
import BackImg from "@assets/images/stepsBack.jpg";
import ExercisePlanCharts from "@features/exercise/ExercisePlanCharts.jsx";
// import CompleteYourChallenges from "./CompleteYourChallenges.jsx";
import { useNavigate } from "react-router-dom";
import LifestyleWellnessOverview from "@features/weeklyProgressTracking/LifeStyleOverview.jsx";
import WeekGraph from "@features/weeklyProgressTracking/WeekGraph.jsx";
import { getWeeklyTracker } from "api/weeklyTracker.js";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader.jsx";
import { useTranslation } from "react-i18next";
import SleepCoachMilestoneModal from "@features/sleep/SleepCoachMilestoneModal";

const WeeklyProgressTracker = () => {
  const [isFieldsLoading, setIsFieldsLoading] = useState(true);
  const [weeklyData, setWeeklyData] = useState(null);
  const [openAchivementModal, setOpenAchivementModal] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const sliderData = [
    {
      RankImage: ConsistancyChamp,
      title: "Consistency Champ",
      unlockDate: "Unlocked on 5th July",
      description:
        " Earned for logging your weekly check-in three weeks in a row.",
    },
    {
      RankImage: BodyProgressHero,
      title: "Body Progress Hero",
      unlockDate: "Unlocked on 5th July",
      description:
        "Unlocked when you achieve measurable improvement in Weight, Body Fat %, or Waist for 3 consecutive weeks.",
    },
    {
      RankImage: BalancedPerformer,
      title: "Balanced Performer",
      unlockDate: "Unlocked on 5th July",
      description:
        "Awarded when your Sleep, Energy, Stress, and Motivation scores all stay above 4 for a week.",
    },
  ];

  // Function to get badge data based on API response or fallback
  const getBadgeData = () => {
    // If API has recent milestone data and it's achieved, use that
    if (weeklyData?.recent_milestone?.achieved) {
      const milestone = weeklyData.recent_milestone;

      // Find matching badge image from sliderData based on title
      const matchedBadge = sliderData.find(
        (item) => item.title === milestone.title
      );

      return {
        RankImage: matchedBadge && matchedBadge.RankImage,
        title: matchedBadge.title,
        unlockDate: `Unlocked on ${new Date().toLocaleDateString("en-US", {
          day: "numeric",
          month: "long",
        })}`,
        description: matchedBadge.description,
        isAchieved: true,
        progress: matchedBadge.progress,
        target: matchedBadge.target,
      };
    }

    // If no recent milestone or not achieved, use first slider item as default
    return {
      ...sliderData[0],
      isAchieved: false,
      progress: 0,
      target: 3,
    };
  };

  const nutrientData = [
    {
      label: t("Weight Change"),
      key: "earnedPoints",
      icon: <WeightChange className="w-[24px] h-[24px]" />,
      // unit: "g",
      tabColor: "#FFF6F0",
      value: weeklyData?.progress_metrics?.["Weight Change"] ?? 0,
    },
    {
      label: t("Body Fat %"),
      key: "quizCompleted",
      icon: <BodyFat className="w-[24px] h-[24px]" />,
      // unit: "g",
      tabColor: "#F0FFFF",
      value: weeklyData?.progress_metrics?.["Body Fat %"] ?? 0,
    },
    {
      label: t("Waist Difference"),
      key: "weeklyRank",
      icon: <WaistChange className="w-[24px] h-[24px]" />,
      // unit: "g",
      tabColor: "#FFF0FB",
      value: weeklyData?.progress_metrics?.["Waist Difference"] ?? 0,
    },
    {
      label: t("Sleep Score"),
      key: "lastPlayed",
      icon: <SleepAvg className="w-[24px] h-[24px]" />,
      // unit: "g",
      tabColor: "#F0FFF1",
      value: weeklyData?.progress_metrics?.["Sleep Quality"] ?? 0,
    },
  ];

  const getData = async () => {
    try {
      setIsFieldsLoading(true);
      const response = await getWeeklyTracker();
      // const tags = response?.selectedPreferences;
      setWeeklyData(response);
      // setOpenAchivementModal(response?.recent_milestone_achieved_now);
    } catch (error) {
      console.error(
        "Error fetching weekly progress:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setIsFieldsLoading(false);
    }
  };
  useEffect(() => {
    getData();
  }, []);

  // Get the current badge data
  const currentBadge = getBadgeData();

  if (isFieldsLoading) {
    return <ActualDietPlanLoader weeklyTracker={true} />;
  }

  // console.log(currentBadge);
  return (
    <div>
      <MainHeader
        heading={t("Track Your Progress, Earn Your Rewards")}
        subheading={t(
          "Stay motivated with streaks, missions, XP, and badges all in one place."
        )}
        description={t(
          "Your journey matters. Keep an eye on your daily streaks, complete simple missions, climb the leaderboard, and unlock unique badges as you go. Every step you take brings you closer to new milestones and rewards."
        )}
        Image={HeaderImage}
        WeeklyProgressTracker={true}
      />
      <div className="bg-white rounded-[12px] px-4 py-5 my-4">
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 py-5 rounded-[12px]"
          style={{ backgroundImage: `url(${BackImg})` }}
        >
          {nutrientData.map(({ label, icon, value, tabColor }, index) => (
            <div
              key={index}
              className="py-8 px-2 rounded-[17px] bg-white border border-[#E6E6E6]  rounded-[16px] flex items-center flex-col xl:flex-row  justify-center gap-2 xl:gap-4"
              style={{
                boxShadow: "7px 7px 9.5px 0px #0000000D",
              }}
            >
              <div className="flex justify-center">
                <div
                  className="rounded-full min-w-[64px] min-h-[64px] max-w-[64px] max-h-[64px] flex items-center justify-center"
                  style={{
                    backgroundColor: tabColor,
                  }}
                >
                  {icon}
                </div>
              </div>
              <div>
                <p className="text-[12px] md:text-[15px] font-[500] text-[#898787] font-[Lato] text-center md:text-start lg:text-center xl:text-start">
                  {label}
                </p>
                <div className="">
                  <p className="text-[16px] md:text-[22px] font-[500] font-[Lato] text-[#6C6A6A] text-center lg:text-center xl:text-start">
                    {value}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-[12px] px-4 py-5 my-4">
        <WeekGraph
          graph_data={weeklyData?.graph_data}
          detailed_metrics={weeklyData?.detailed_metrics}
          total_weeks_tracked={weeklyData?.summary?.total_weeks_tracked}
          physical_trans_unit={weeklyData?.physical_trans_unit}
        />
        <div className="grid grid-cols-1 xl:grid-cols-7 gap-5 my-5">
          <div className="md:col-span-4">
            <LifestyleWellnessOverview
              metrics={weeklyData?.lifestyle_wellness}
              t={t}
            />
          </div>

          <div
            className="p-6 rounded-xl text-white bg-cover xl:h-full relative md:col-span-3"
            style={{ backgroundImage: `url(${BackImg})` }}
          >
            <div className="flex justify-between items-center mb-10">
              <h4 className="text-[16px] font-[Lato] text-white">
                {t("quizGame.Recent Milestone Achieved")}
              </h4>
              <button
                className="bg-white rounded-md text-[#0575e6] text-[12px] font-[Lato] py-2 px-3"
                onClick={() => navigate("/progress-tracker/ranks")}
              >
                {t("quizGame.View All")}
              </button>
            </div>
            <div className="flex justify-center w-full mb-10">
              <div
                className={`${
                  !currentBadge?.isAchieved && "blur-[8px]"
                }  bg-white rounded-3xl w-[85%] p-5 relative pb-8 `}
              >
                {" "}
                {/* Added pb-8 for bottom padding */}
                <div className="flex justify-center mt-[35%] sm:mt-[18%] lg:mt-[25%] xl:mt-[30%]">
                  <h3
                    className={`${
                      !currentBadge?.isAchieved
                        ? "text-white bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[22px] w-[fit-content] py-2 px-3 my-2"
                        : "text-[#000]"
                    }
                      text-[16px] font-[600] font-[Lato] text-center`}
                  >
                    {currentBadge?.title}
                  </h3>
                </div>
                <div className="flex justify-center">
                  {currentBadge?.isAchieved && (
                    <button
                      disabled
                      className={`rounded-[22px] py-2 px-4 text-[12px] my-2 ${
                        currentBadge.isAchieved
                          ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white"
                          : "bg-gray-300 text-gray-600"
                      }`}
                    >
                      {currentBadge.isAchieved ? "Unlocked" : "Locked"}
                    </button>
                  )}
                </div>
                <div className="border border-[#DDDDDD] p-2 rounded-xl">
                  <p className="text-[12px] text-[#5C5C5C] text-center">
                    {currentBadge?.description}
                  </p>
                  {/* Show progress if available */}
                  {/* {currentBadge.progress !== undefined && (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ 
                            width: `${(currentBadge.progress / currentBadge.target) * 100}%` 
                          }}
                        ></div>
                      </div>
                      <p className="text-[10px] text-[#5C5C5C] text-center mt-1">
                        Progress: {currentBadge.progress}/{currentBadge.target}
                      </p>
                    </div>
                  )} */}
                </div>
                <img
                  src={currentBadge?.RankImage}
                  alt={currentBadge?.title}
                  className={`absolute -top-[8%] left-1/2 -translate-x-1/2 z-[9] lg:w-[30%] xl:w-[37%] object-cover" `}
                  style={{ maxHeight: "none" }}
                />
              </div>
            </div>

            {!currentBadge?.isAchieved && (
              <img
                src={LockSleepCoach}
                alt="image"
                className="absolute top-[40%] 2xl:top-[30%]  left-1/2 -translate-x-1/2 z-[9] object-cover w-[40px] sm:w-[50px]"
              />
            )}
          </div>
        </div>
      </div>
      {/* <CompleteYourChallenges /> */}
      <ExercisePlanCharts />

      {/* <SleepCoachMilestoneModal
        isOpen={openAchivementModal}
        setIsOpen={setOpenAchivementModal}
        item={currentBadge}
        progress={true}
      /> */}
    </div>
  );
};

export default WeeklyProgressTracker;
